import * as React from 'react';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useChangeWorkspaceRole } from '~teacher/hook/workspaces/useChangeWorkspaceRole';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';
import { useRemoveWorkspaceMember } from '~teacher/hook/workspaces/useRemoveWorkspaceMember';
import { useTransferWorkspaceOwnership } from '~teacher/hook/workspaces/useTransferWorkspaceOwnership';

import { isAxiosError } from '../../../../../api/isAxiosError';
import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';

/**
 * unified hook for handling workspace members actions
 * - independent action for removing a workspace member
 * - transfer ownership of workspace, trigger modal
 */
export const useWorkspaceActions = (workspaceId: string) => {
  const socrativeRouter = useSocrativeRouter();
  const {
    permissions: {
      editMemberRoles: canEditMemberRoles,
      transferWorkspaceOwnership: canTransferWorkspaceOwnership,
      removeMemberFromWorkspace: canRemoveMemberFromWorkspace,
    },
  } = useWorkspacePermissionsV2({ workspaceId });

  const requestUser = useGetWorkspaceRole({ workspaceId });
  const removeMemberMutation = useRemoveWorkspaceMember(workspaceId);
  const changeWorkspaceRoleMutation = useChangeWorkspaceRole(workspaceId);
  const transferMemberMutation = useTransferWorkspaceOwnership();

  // initial states
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [isTransferOpen, setIsTransferOpen] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(false);

  // using refs to prevent these from changing when the op is not completed
  const userId = React.useRef<null | string>(null);
  const username = React.useRef<null | string>(null);

  // on close
  const onTransferClose = React.useCallback(() => {
    setIsTransferOpen(false);
  }, []);
  const onRemoveClose = React.useCallback(() => {
    setIsRemoveOpen(false);
  }, []);

  // actions
  const onTransfer = React.useCallback(() => {
    setIsDisabled(true);
    transferMemberMutation.mutate(
      {
        workspaceId,
        userId: userId.current,
      },
      {
        onSettled: () => {
          setIsDisabled(false);
          onTransferClose();
        },
        onError: () => toast.critical(translate('An error has occurred')),
      }
    );
  }, [onTransferClose, transferMemberMutation, workspaceId]);

  const onChangeRole = React.useCallback(
    ({ userId, role }: { userId: string; role: WorkspaceRole }) => {
      changeWorkspaceRoleMutation.mutate(
        {
          workspaceId,
          userId,
          role,
        },
        {
          onError: (err) => {
            if (isAxiosError(err)) {
              switch (err.response.status) {
                case 403: {
                  toast.critical(translate('An error has occurred'));
                  break;
                }
                default:
                  toast.critical(
                    translate(
                      'An error occurred while trying to change access level'
                    )
                  );
              }
            } else {
              toast.critical(
                'An error occurred while trying to change access level'
              );
            }
          },
        }
      );
    },
    [changeWorkspaceRoleMutation, workspaceId]
  );

  const onRemove = React.useCallback(() => {
    setIsDisabled(true);
    removeMemberMutation.mutate(
      {
        workspaceId,
        userId: userId.current,
      },
      {
        onSettled: () => {
          setIsDisabled(false);
        },
        onSuccess: (_data, { userId }) => {
          if (userId === requestUser.data?.id) {
            socrativeRouter.navigate('launch', {
              trigger: true,
              replace: true,
            });
          } else {
            onRemoveClose();
          }
        },
        onError: () => toast.critical(translate('An error has occurred')),
      }
    );
  }, [
    onRemoveClose,
    removeMemberMutation,
    requestUser.data?.id,
    socrativeRouter,
    workspaceId,
  ]);

  // action options
  const tableActions = React.useMemo(
    () => [
      {
        title: translate('Admin'),
        heading: translate('Change role'),
        description: translate('Manage members, settings, and all content'),
        selected: ({ data }) => data.role === WorkspaceRole.Admin,
        searchKey: 'admin',
        key: 'admin',
        action: ({ close, data }) => {
          onChangeRole({ userId: data.id, role: WorkspaceRole.Admin });
          close();
        },
        constraint: ({ data }) =>
          canEditMemberRoles && data.id !== requestUser.data?.id,
      },
      {
        title: translate('Editor'),
        description: translate(
          'Create and edit content. Can invite editors, viewers, and guests.'
        ),
        selected: ({ data }) => data.role === WorkspaceRole.Editor,
        searchKey: 'editor',
        key: 'editor',
        action: ({ close, data }) => {
          onChangeRole({ userId: data.id, role: WorkspaceRole.Editor });
          close();
        },
        constraint: ({ data }) =>
          canEditMemberRoles && data.id !== requestUser.data?.id,
      },
      {
        title: translate('Viewer'),
        description: translate(
          'Launch quizzes. Cannot edit. Can invite other viewers.'
        ),
        selected: ({ data }) => data.role === WorkspaceRole.Viewer,
        searchKey: 'viewer',
        key: 'viewer',
        action: ({ close, data }) => {
          onChangeRole({ userId: data.id, role: WorkspaceRole.Viewer });
          close();
        },
        constraint: ({ data }) =>
          canEditMemberRoles && data.id !== requestUser.data?.id,
      },
      {
        title: translate('Transfer ownership'),
        description: ({ data }) =>
          translate("Make {0} the owner. You'll become an Admin.").format(
            data.fullName
          ),
        divider: true,
        searchKey: 'transfer ownership',
        key: 'transfer-ownership',
        action: ({ close, data }) => {
          userId.current = data.id;
          username.current = data.fullName;
          setIsTransferOpen(true);
          close();
        },
        constraint: () => canTransferWorkspaceOwnership,
      },
      {
        // Own row is a self-leave, others are a removal.
        title: ({ data }) =>
          data.id === requestUser.data?.id
            ? translate('Leave library')
            : translate('Remove from library'),
        divider: true,
        disabled: ({ data }) => {
          // Do not disable for the current Teacher
          if (data.id === requestUser.data?.id) {
            return false;
          }
          return !canRemoveMemberFromWorkspace;
        },
        // No description on a self-leave — only when removing another member.
        description: ({ data }) =>
          data.id === requestUser.data?.id
            ? undefined
            : translate(
                'This member will lose access to the library and its quiz reports.'
              ),
        color: 'text-red-800',
        searchKey: 'remove',
        key: 'remove',
        action: ({ close, data }) => {
          userId.current = data.id;
          username.current = data.fullName;
          setIsRemoveOpen(true);
          close();
        },
      },
    ],
    [
      canEditMemberRoles,
      canTransferWorkspaceOwnership,
      onChangeRole,
      requestUser.data?.id,
      canRemoveMemberFromWorkspace,
    ]
  );
  // exports
  return React.useMemo(
    () => ({
      isDisabled,
      onTransfer,
      onRemove,
      username: username.current,
      // The remove dialog also handles a member leaving themselves; surface
      // whether the pending target is the current user so the dialog can show
      // second-person copy. `userId.current` is set before `isRemoveOpen`
      // flips, so this is current whenever the dialog is open.
      isRemovingSelf: userId.current === requestUser.data?.id,
      tableActions,
      isRemoveOpen,
      isTransferOpen,
      onTransferClose,
      onRemoveClose,
    }),
    [
      isDisabled,
      onTransfer,
      onRemove,
      username,
      requestUser.data?.id,
      tableActions,
      isRemoveOpen,
      isTransferOpen,
      onTransferClose,
      onRemoveClose,
    ]
  );
};
